import { mintWsTicket, WsTicketError } from "./session-api.js";

/**
 * The longest one legacy collaboration sign-in keeps asking for its ticket.
 * The live canvas rejoins at most twice after a refused sign-in, after 1 s and
 * 2 s (collab-client `legacy-provider-recovery.ts`), so three sign-ins and both
 * waits fit inside the 15 s a person waits for both screens to match
 * (NET-BUG-28).
 */
export const LEGACY_COLLAB_TICKET_BUDGET_MS = 3_000;

/** The waits before the second, third and fourth ask. */
const RETRY_DELAYS_MS: readonly number[] = [250, 500, 1_000];

/**
 * The ticket a legacy collaboration sign-in sends (NET-BUG-28). Collaboration
 * only: the notification bell keeps its single ask.
 *
 * - No answer, a 5xx or a 429 is asked again within the budget, never sooner
 *   than the server's `Retry-After`. When the budget runs out the error is
 *   thrown, and the canvas counts the attempt as one refused sign-in.
 * - A request still open when the budget runs out is aborted, and the ask
 *   ends then with a status-0 `WsTicketError`, even if the request never
 *   answers its abort.
 * - A 401 or 403 is a final answer: `null`, which the canvas takes as "no
 *   access" and stops at once, showing Retry.
 * - Any other failure is thrown at once.
 */
export async function legacyCollabTicket(): Promise<string | null> {
  const deadline = Date.now() + LEGACY_COLLAB_TICKET_BUDGET_MS;
  for (let attempt = 0; ; attempt += 1) {
    try {
      return (await askBefore(deadline)).ticket;
    } catch (error) {
      if (!(error instanceof WsTicketError)) throw error;
      if (error.status === 401 || error.status === 403) return null;
      const delay = RETRY_DELAYS_MS[attempt];
      if (!isRetryable(error.status) || delay === undefined) throw error;
      const wait = Math.max(delay, (error.retryAfterSeconds ?? 0) * 1_000);
      if (Date.now() + wait > deadline) throw error;
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  }
}

/** One ask, which gives up at the deadline whether or not it was answered. */
async function askBefore(deadline: number): ReturnType<typeof mintWsTicket> {
  const request = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(
        new WsTicketError(0, null, "legacyCollabTicket: no answer in time"),
      );
      request.abort();
    }, deadline - Date.now());
  });
  try {
    return await Promise.race([
      mintWsTicket({ signal: request.signal }),
      expired,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function isRetryable(status: number): boolean {
  return status === 0 || status === 429 || status >= 500;
}
